/**
 * Read identity facts off a Clerk user for analytics: the email to attribute
 * events to, how the account signed up, and whether this session is the one
 * that created the account.
 *
 * Kept structural (not `UserResource`) so callers and tests can pass the plain
 * subset of the Clerk user they have.
 */

export interface ClerkIdentityUser {
  id: string;
  firstName?: string | null;
  lastName?: string | null;
  createdAt?: Date | null;
  lastSignInAt?: Date | null;
  primaryEmailAddress?: { emailAddress?: string | null } | null;
  emailAddresses?: Array<{ emailAddress?: string | null }> | null;
  externalAccounts?: Array<{ provider?: string | null; emailAddress?: string | null }> | null;
}

/**
 * How long after account creation a sign-in still counts as the signup.
 * Clerk stamps both `createdAt` and `lastSignInAt` server-side when a signup
 * finishes, so they are seconds apart for a new account and far apart for
 * every later sign-in.
 */
export const SIGNUP_SESSION_WINDOW_MS = 60 * 1000;

/**
 * How long after account creation this browser still reports the signup.
 * `lastSignInAt` only moves on a fresh sign-in, so it stays next to
 * `createdAt` for the whole first Clerk session, which can last days. This
 * bound uses the device clock, so it is wide enough to absorb clock drift.
 */
export const SIGNUP_REPORT_WINDOW_MS = 30 * 60 * 1000;

export const EMAIL_SIGNUP_METHOD = "email";

/**
 * Clerk can hand over a user whose `primaryEmailAddress` is not linked yet
 * (seen right after OAuth signups). The address is still on the user's
 * email list or on the OAuth account, so fall back to those.
 */
export const getClerkUserEmail = (user: ClerkIdentityUser | null | undefined): string | null =>
  user?.primaryEmailAddress?.emailAddress ||
  user?.emailAddresses?.find((address) => address.emailAddress)?.emailAddress ||
  user?.externalAccounts?.find((account) => account.emailAddress)?.emailAddress ||
  null;

/**
 * The account's first and last name, keyed as analytics expects. Blank names
 * are left out so a reader can fall back to another source instead of
 * receiving an empty string.
 */
export const getClerkUserNames = (
  user: ClerkIdentityUser | null | undefined,
): { first_name?: string; last_name?: string } => {
  const names: { first_name?: string; last_name?: string } = {};
  const first = user?.firstName?.trim();
  const last = user?.lastName?.trim();
  if (first) names.first_name = first;
  if (last) names.last_name = last;
  return names;
};

/** The OAuth provider the account signed up with (e.g. "google"), else "email". */
export const getClerkSignupMethod = (user: ClerkIdentityUser | null | undefined): string =>
  user?.externalAccounts?.find((account) => account.provider)?.provider || EMAIL_SIGNUP_METHOD;

/**
 * True only in the first minutes of the session that created this Clerk
 * account. Falls back to the device clock alone if Clerk has not stamped
 * `lastSignInAt` yet.
 */
export const isClerkSignupSession = (user: ClerkIdentityUser | null | undefined): boolean => {
  if (!user?.createdAt) return false;
  const createdAt = new Date(user.createdAt).getTime();
  const now = Date.now();
  if (Math.abs(now - createdAt) >= SIGNUP_REPORT_WINDOW_MS) return false;
  const signedInAt = user.lastSignInAt ? new Date(user.lastSignInAt).getTime() : now;
  return Math.abs(signedInAt - createdAt) < SIGNUP_SESSION_WINDOW_MS;
};
